import { useRef, useState, type RefObject } from "react";
import { saveAs } from "file-saver";
import {
  ChevronUpIcon,
  CopyIcon,
  DownloadIcon,
  EllipsisIcon,
} from "lucide-react";

import { AudioPlayer, Column, RichTextDisplay, Row, Text } from "@/components";
import { CallIcon } from "@/components/CallIcon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { misc } from "@/lib/misc";
import { transcript } from "@/lib/transcript";
import { cn } from "@/lib/utils";
import { CallDirection } from "@/types";

type CallDetailSection = "summary" | "transcript";

export const CallRecordingCard = ({
  audioRef,
  audioSrc,
  dateLabel,
  direction,
  downloadFilename = "call-audio.mp3",
  getRoleLabel,
  hideCallIdentity = false,
  hideHeader = false,
  isMissed = false,
  isScheduled = false,
  isVoicemail = false,
  job,
  summary,
  title,
  transcriptLines,
  type,
}: {
  // Lets a host share one player with content outside this card, so a citation
  // elsewhere on the page can seek this call.
  audioRef?: RefObject<HTMLAudioElement>;
  audioSrc?: string | null;
  dateLabel: string;
  // Only staffer calls have a direction; AI calls are always outbound.
  direction?: CallDirection;
  downloadFilename?: string;
  getRoleLabel: (role: string) => string;
  // Drops the icon/title/type row (and the job block) for hosts whose
  // surroundings already say what the call is. The date and actions stay.
  hideCallIdentity?: boolean;
  // Drops the whole header row, for hosts that render their own.
  hideHeader?: boolean;
  // A call attempt with nothing to play back or read — still shown, just dimmed.
  isMissed?: boolean;
  // Booked but not placed yet. Shown at full strength with the scheduled
  // icon, so it doesn't read as a call that failed.
  isScheduled?: boolean;
  isVoicemail?: boolean;
  job?: { company: string; title: string };
  summary?: string | null;
  title: string;
  transcriptLines?: { content: string; role: string; time: string }[] | null;
  type: "ai" | "staffer";
}) => {
  /* React Hooks */
  const internalAudioRef = useRef<HTMLAudioElement>(null);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const playerAudioRef = audioRef || internalAudioRef;
  const successToast = useSuccessToast();
  // Both sections start collapsed; clicking a tab opens it, clicking the
  // active tab collapses again.
  const [activeSection, setActiveSection] = useState<CallDetailSection>();

  /* Helpers */
  const typeLabel = type === "ai" ? "AI call" : "Staffer call";
  const hasTranscript = !!transcriptLines?.length;
  const hasSummary = !!summary?.trim();

  /* Handlers */
  const handleTranscriptTimestampClick = (time: string) => {
    if (!playerAudioRef.current) return;

    const timeInSeconds = transcript.convertTimestampToSeconds(time);
    if (timeInSeconds === null) return;

    playerAudioRef.current.currentTime = timeInSeconds;
    void playerAudioRef.current.play();
  };

  const handleSectionTabClick = (section: CallDetailSection) => {
    setActiveSection((current) => (current === section ? undefined : section));
  };

  /* Render */
  const renderActionsMenu = () => {
    if (!hasTranscript && !audioSrc) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-4 w-4 shrink-0 p-0" size="icon" variant="ghost">
            <EllipsisIcon className="h-4 w-4 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasTranscript ? (
            <DropdownMenuItem
              onClick={() => {
                const text = misc.buildClipboardTextForTranscript({
                  getRoleLabel,
                  transcriptLines: transcriptLines,
                });
                void navigator.clipboard.writeText(text);
                successToast({
                  description: "Copied transcript to clipboard!",
                });
              }}
            >
              <CopyIcon className="h-4 w-4" />
              Copy full transcript
            </DropdownMenuItem>
          ) : null}
          {audioSrc ? (
            <DropdownMenuItem
              onClick={() => {
                saveAs(audioSrc, downloadFilename);
              }}
            >
              <DownloadIcon className="h-4 w-4" />
              Download audio
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTranscript = () => {
    if (!transcriptLines) return null;

    return (
      <Column className="gap-2">
        {transcriptLines.map((line, index) => (
          <div
            className="grid grid-cols-[40px_1fr] items-start gap-6"
            key={`${line.time}-${String(index)}`}
          >
            <Button
              className="h-auto p-0 text-xs font-medium text-primary-900"
              onClick={() => {
                handleTranscriptTimestampClick(line.time);
              }}
              type="button"
              variant="link"
            >
              {line.time}
            </Button>
            <Text className="text-sm font-normal leading-relaxed text-[#121212] dark:text-[#FAFAFA]">
              {`${getRoleLabel(line.role)}: ${line.content}`}
            </Text>
          </div>
        ))}
      </Column>
    );
  };

  const renderSummaryText = () => {
    if (!summary) return null;

    // Summaries are stored as rich-text HTML.
    return (
      <RichTextDisplay
        className="text-sm font-normal leading-relaxed text-[#121212] dark:text-[#FAFAFA]"
        richText={summary}
      />
    );
  };

  const renderSectionTab = (section: CallDetailSection, label: string) => {
    const isActive = activeSection === section;

    return (
      <Button
        className={cn(
          "h-auto gap-1 rounded-none border-b-2 border-transparent p-0 pb-1 text-xs font-normal text-slate-400 hover:bg-transparent hover:text-slate-700",
          isActive && "border-primary-900 font-medium text-primary-900",
        )}
        onClick={() => {
          handleSectionTabClick(section);
        }}
        type="button"
        variant="ghost"
      >
        {label}
        {/* Signals the active tab collapses on re-click. */}
        {isActive ? <ChevronUpIcon className="h-3 w-3 shrink-0" /> : null}
      </Button>
    );
  };

  const renderDetailSections = () => {
    if (!hasTranscript && !hasSummary) return null;

    return (
      <Column className="gap-3">
        <Row className="items-center gap-4">
          {hasTranscript ? renderSectionTab("transcript", "Transcript") : null}
          {hasSummary ? renderSectionTab("summary", "Summary") : null}
        </Row>
        {activeSection === "transcript" ? renderTranscript() : null}
        {activeSection === "summary" ? renderSummaryText() : null}
      </Column>
    );
  };

  const renderHeader = () => {
    return (
      <Column className="gap-2 px-5 pb-4 pt-5">
        <Row className="items-center justify-between gap-3 py-1">
          <Text className="w-fit rounded bg-slate-100 px-1.5 py-0.5 font-plex-mono text-[10px] font-medium uppercase tracking-wider text-slate-950">
            {dateLabel}
          </Text>
          {renderActionsMenu()}
        </Row>
        {hideCallIdentity ? null : renderIdentityRow()}
      </Column>
    );
  };

  const renderIdentityRow = () => {
    return (
      <Row className="items-center justify-between gap-4">
        <Row className="min-w-0 items-center gap-3">
          <CallIcon
            direction={direction}
            isAi={type === "ai"}
            isMissed={isMissed}
            isScheduled={isScheduled}
            isVoicemail={isVoicemail}
          />
          <Row className="min-w-0 flex-1 flex-wrap items-center gap-2">
            <Text className="text-sm font-medium text-slate-700">{title}</Text>
            <Text className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-normal text-slate-600">
              {typeLabel}
            </Text>
          </Row>
        </Row>
        {job ? (
          <Column className="min-w-0 shrink-0 items-end gap-0.5 text-right">
            <Text className="max-w-52 truncate text-sm font-medium text-slate-700">
              {job.title}
            </Text>
            <Text className="max-w-52 truncate text-xs font-normal text-slate-400">
              {job.company}
            </Text>
          </Column>
        ) : null}
      </Row>
    );
  };

  const render = () => {
    return (
      <Column
        className={cn(
          "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm",
          isMissed && "opacity-60",
        )}
      >
        {hideHeader ? null : renderHeader()}
        {audioSrc || hasTranscript || hasSummary ? (
          <Column
            className={cn(
              "gap-3 px-3 py-4",
              type === "ai"
                ? "bg-[linear-gradient(286deg,#e2edff_1.28%,#eef7ff_100%)]"
                : "bg-slate-50",
            )}
          >
            {audioSrc ? (
              <AudioPlayer audioRef={playerAudioRef} audioSrc={audioSrc} />
            ) : null}
            {renderDetailSections()}
          </Column>
        ) : null}
      </Column>
    );
  };

  return render();
};
